import { BlobProvider } from "@react-pdf/renderer";
import Dropdown from "components/common/Dropdown";
import React, { useEffect, useState } from "react";
import { firebaseService } from "services/firebaseService";
import PurchaseOrdersPDF from "components/views/Ldb/Orders/PDFPurchase";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { getFromStorage } from "helpers/localStorageUtils";
import { orderActions } from "redux/Ldb/actions";
import CustomToast from "components/common/CustomToast";
import toast from "react-hot-toast";
import Button from "components/common/Button";
import extractFirebasePath from "helpers/extractFirebasePath";
import { useNavigate } from "react-router-dom";
import ModalContainer from "components/common/ModalContainer";
import { AccountPermissions } from "types/permissions";
import TextareaInput from "components/common/InputField/TextareaInput";

type PurchaseOrderTableActionProps = {
    po: any;
    refreshList: () => void;
    setSelectedPOs: (po: any) => void;
    setArchiveModal: (po: any) => void;
    setUnarchiveModal: (po: any) => void;
    setSinglePOArchive: (po: string[]) => void;
    setSinglePOUnarchive: (po: string[]) => void;
};

export default function PurchaseOrderTableAction(props: PurchaseOrderTableActionProps) {
    const {
        po,
        refreshList,
        setSelectedPOs,
        setArchiveModal,
        setUnarchiveModal,
        setSinglePOArchive,
        setSinglePOUnarchive,
    } = props;
    const purchaseOrderList = po;

    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [openApprovalSuccessModal, setOpenApprovalSuccessModal] = useState(false);
    const [openRejectModal, setOpentRejectModal] = useState(false);
    const [openRejectionReasonModal, setOpentRejectionReasonModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");
    const [selectedPO, setSelectedPO] = useState<any>(null);

    const [userProfile, setUserProfile] = useState<{ [key: string]: any }>(() =>
        getFromStorage("ally-user")
    );
    const [subTotals, setSubTotals] = useState<number>(0);

    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const {
        fetchingSinglePurchaseOrder,
        fetchedSinglePurchaseOrderSuccess,
        loadingPOApprovalOrRejection,
        approveOrRejectPOSuccess,
    } = useAppSelector((state) => state.order);

    let totalSubtotals = 0;

    // if (purchaseOrderList?.itemsOrServices?.length > 0) {
    //     for (const container of purchaseOrderList?.itemsOrServices) {
    //         const subtotal = parseFloat(container.subtotal.amount);
    //         totalSubtotals += subtotal;
    //     }

    //     setSubTotals(totalSubtotals);
    // }

    const container = purchaseOrderList?.itemsOrServices?.map((data, idx) => ({
        productImageDetails: {
            avatar: data?.inventory?.productImageDetails?.productAvatar,
        },
        productName: data?.inventory?.productName,
        sku: data?.inventory?.sku,
        description: data?.description,
        unitCost: {
            amount: data?.price?.amount,
            currency: data?.price?.currency,
        },
        subtotal: {
            amount: data?.subtotal?.amount,
        },
        quantity: data?.qty,
        deliveryDate: data?.deliveryDate,
        leadTime: data?.leadTime,
    }));

    const customerDetails = {
        issuedDate: purchaseOrderList?.issuedDate,
        dueDate: purchaseOrderList?.dueDate,
        shippingTerms: purchaseOrderList?.shippingTerms,
        fobPoint: purchaseOrderList?.fobPoint,
        shippingVia: purchaseOrderList?.shippingMethod,
        source: purchaseOrderList?.source,
        currency: purchaseOrderList?.currency,
        tax: purchaseOrderList?.tax?.percentage,
        shippingAndHandling: purchaseOrderList?.shippingAndHandling?.amount,
        termsAndCondition: purchaseOrderList?.termsAndCondition,
        paymentTerms: purchaseOrderList?.paymentTerms,
        incoterm: purchaseOrderList?.incoterm,
        additionalInfo: purchaseOrderList?.additionalComment,
    };

    const pdfName =
        purchaseOrderList?.documents?.[0]?.filename === "purchase-order-doc" ||
        purchaseOrderList?.documents?.[0]?.path?.includes("tradeally") ||
        purchaseOrderList?.documents?.length === 0 ||
        purchaseOrderList?.documents === null ||
        purchaseOrderList?.documents === undefined
            ? extractFirebasePath(
                  purchaseOrderList?.documents?.length === 0 ||
                      purchaseOrderList?.documents === null ||
                      purchaseOrderList?.documents === undefined
                      ? ""
                      : purchaseOrderList?.documents?.[0]?.path
              )
            : purchaseOrderList?.documents?.[0]?.filename;

    const handleApproveRejectPO = (action: "Approve" | "Reject", rejectionReason?: string) => {
        setSelectedPO(po);
        const specificTask = purchaseOrderList?.tasks?.find(
            (task) => task?.assignee?._id === userProfile?.user?._id
        );
        const body: { [key: string]: any } = {
            approved: action === "Approve",
            taskId: specificTask?._id,
            poId: po?._id,
        };

        if (action === "Reject") {
            body.rejectReason = rejectionReason;
        }

        dispatch(orderActions.approveRejectPO(body));
    };

    useEffect(() => {
        if (Boolean(approveOrRejectPOSuccess)) {
            const isApprovedAction = approveOrRejectPOSuccess?.po?.approvedById?.includes(
                userProfile?.user?._id
            );

            const isRejectionAction = approveOrRejectPOSuccess?.po?.rejectedById?.includes(
                userProfile?.user?._id
            );

            if (isApprovedAction) {
                setOpenApproveModal(false);
                setOpenApprovalSuccessModal(true);
            }

            if (isRejectionAction) {
                setOpentRejectModal(false);
                setOpentRejectionReasonModal(false);
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message={"Purchase order rejected successfully"}
                        type={"error"}
                    />
                ));
            }
            setRejectionReason("");
            dispatch(orderActions.resetApproveRejectPOSuccess());
            refreshList();
        }
    }, [approveOrRejectPOSuccess, dispatch, userProfile?.user?._id]);

    const quantities = purchaseOrderList?.itemsOrServices?.map((item) => Number(item.qty));
    const totalQuantity = quantities?.reduce((acc, curr) => acc + curr, 0);

    const DownloadPo = () => (
        <BlobProvider
            document={
                <PurchaseOrdersPDF
                    imageDetails={purchaseOrderList?.ldb?.profile?.avatar}
                    customerDetails={customerDetails}
                    ldbName={purchaseOrderList?.ldb?.profile?.businessName}
                    ldbEmail={purchaseOrderList?.ldb?.email}
                    ldbPhone={purchaseOrderList?.ldb?.mobile}
                    ldbAddress={purchaseOrderList?.ldb?.profile?.address?.address}
                    vendorName={purchaseOrderList?.vendor?.profile?.businessName}
                    vendorEmail={purchaseOrderList?.vendor?.email}
                    vendorAddress={purchaseOrderList?.vendor?.profile?.address?.address}
                    vendorPhone={purchaseOrderList?.vendor?.mobile}
                    totalQuantity={totalQuantity}
                    totalAmount={subTotals}
                    filesAttached={purchaseOrderList?.attachments}
                    taxAmount={purchaseOrderList?.tax?.amount}
                    taId={purchaseOrderList?.taId}
                    poPrefix={purchaseOrderList?.poPrefix}
                    containers={container}
                />
            }
        >
            {({ blob }) => {
                console.log({ blob });
                return (
                    <div className={`w-full !px-4 py-2`} onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                            dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                            dropdownContainerClasses={`shadow-cardShadow-2 border-0 rounded !max-h-fit left-[-100px] `}
                            dropdownItemsClasses={``}
                            name={"faq-dropdown"}
                            dropdownOptions={[
                                {
                                    label: "Edit PO",
                                    value: "edit",
                                    disabled: po?.isArchived ? true : false,
                                    action: () => {
                                        navigate(
                                            `/dashboard/purchase/edit-purchase-request/${po?._id}`,
                                            {
                                                state: { internalNavigation: true, edit: true },
                                            }
                                        );
                                    },
                                },
                                // {
                                //     label: "Download",
                                //     value: "download",
                                //     action: () => {
                                //         console.log({ blob });
                                //     },
                                // },
                                ...(purchaseOrderList?.currentStatus === "pending"
                                    ? [
                                          {
                                              label: "Approve Po",
                                              value: "approve",
                                              disabled: po?.isArchived ? true : false,

                                              action: () => {
                                                  handleApproveRejectPO("Approve", po._id);
                                              },
                                          },
                                          {
                                              label: "Reject PO",
                                              value: "reject",
                                              disabled: po?.isArchived ? true : false,

                                              labelContainerClassName: "!text-[red]",
                                              labelClassName: "!text-[red]",
                                              action: () => {
                                                  handleApproveRejectPO("Reject", po._id);
                                              },
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </div>
                );
            }}
        </BlobProvider>
    );
    return (
        <>
            {" "}
            <BlobProvider
                document={
                    <PurchaseOrdersPDF
                        imageDetails={purchaseOrderList?.ldb?.profile?.avatar}
                        customerDetails={customerDetails}
                        ldbName={purchaseOrderList?.ldb?.profile?.businessName}
                        ldbEmail={purchaseOrderList?.ldb?.email}
                        ldbPhone={purchaseOrderList?.ldb?.mobile}
                        ldbAddress={purchaseOrderList?.ldb?.profile?.address?.address}
                        vendorName={purchaseOrderList?.vendor?.profile?.businessName}
                        vendorEmail={purchaseOrderList?.vendor?.email}
                        vendorAddress={purchaseOrderList?.vendor?.profile?.address?.address}
                        vendorPhone={purchaseOrderList?.vendor?.mobile}
                        totalQuantity={totalQuantity}
                        totalAmount={subTotals}
                        filesAttached={purchaseOrderList?.attachments}
                        taxAmount={purchaseOrderList?.tax?.amount}
                        taId={purchaseOrderList?.taId}
                        poPrefix={purchaseOrderList?.poPrefix}
                        containers={container}
                    />
                }
            >
                {({ blob }) => {
                    console.log({ blob });
                    return (
                        <div className={`w-full !px-4 py-2`} onClick={(e) => e.stopPropagation()}>
                            <Dropdown
                                dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                                dropdownClassName={"!w-full !mb-10 !-mt-7"}
                                dropdownContainerClasses={`shadow-cardShadow-2 border-0 rounded !max-h-fit left-[-100px] `}
                                dropdownItemsClasses={``}
                                name={"faq-dropdown"}
                                dropdownOptions={[
                                    {
                                        label: "Edit PO",
                                        value: "edit",
                                        disabled: po?.isArchived ? true : false,

                                        action: () => {
                                            navigate(
                                                `/dashboard/purchase/edit-purchase-request/${po?._id}`,
                                                {
                                                    state: { internalNavigation: true, edit: true },
                                                }
                                            );
                                        },
                                    },
                                    // {
                                    //     label: "Download",
                                    //     value: "download",
                                    //     action: () => {
                                    //         console.log({ blob });
                                    //     },
                                    // },
                                    // ...(purchaseOrderList?.status?.toLocaleLowerCase() === "pending"
                                    //     ? [
                                    {
                                        label: "Approve Po",
                                        value: "approve",
                                        disabled: po?.isArchived ? true : false,

                                        action: () => {
                                            handleApproveRejectPO("Approve", po._id);
                                        },
                                    },
                                    {
                                        label: `${po?.isArchived ? "Unarchive" : "Archive"} PO`,
                                        value: "archive",

                                        action: () => {
                                            setSelectedPOs([]);
                                            if (po?.isArchived) {
                                                setSinglePOUnarchive([po._id]);
                                                setUnarchiveModal(true);
                                            } else {
                                                setSinglePOArchive([po._id]);
                                                setArchiveModal(true);
                                            }
                                        },
                                    },
                                    {
                                        label: "Reject PO",
                                        value: "reject",
                                        disabled: po?.isArchived ? true : false,
                                        action: () => {
                                            handleApproveRejectPO("Reject", po._id);
                                        },
                                    },

                                    //   ]
                                    // : []),
                                ]}
                            />
                        </div>
                    );
                }}
            </BlobProvider>
            <ModalContainer
                open={openApproveModal && selectedPO?._id === po?._id}
                showCloseIcon={false}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[500px]"
                closeModal={() => {
                    setOpenApproveModal(false);
                }}
            >
                <div className="bg-white !px-2 p-8  rounded-[8px]">
                    <div className="mt-4 px-6">
                        <div className="text-center flex justify-center flex-col items-center space-y-5">
                            <div className="w-16 h-16 flex justify-center items-center bg-[#00DB8F] rounded-full">
                                <i className="ri-checkbox-circle-fill text-[29px] text-white"></i>
                            </div>
                            <div>
                                <h3 className="text-2xl mb-2 text-black font-rocGroteskMedium">
                                    Approved
                                </h3>
                                <p className="text-slate-500 text-center mt-1 text-sm font-rocGroteskMedium">
                                    The purchase request has been approved by you, kindly proceed to
                                    the next approval level.
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 flex gap-3">
                            <Button
                                btnText={"Close"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !text-gm-50"}
                                isLoading={false}
                                onClick={() => {
                                    setOpenApproveModal(false);
                                }}
                                disabled={loadingPOApprovalOrRejection}
                            />
                            <Button
                                onClick={() => handleApproveRejectPO("Approve", po._id)}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_purchase_order,
                                ]}
                                btnText={"Proceed"}
                                type={"button"}
                                disabled={loadingPOApprovalOrRejection}
                                isLoading={loadingPOApprovalOrRejection}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
            <ModalContainer
                open={openRejectModal && selectedPO?._id === po?._id}
                showCloseIcon={false}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[500px]"
                closeModal={() => {
                    setOpentRejectModal(false);
                }}
            >
                <div className="bg-white !px-2 p-8  rounded-[8px]">
                    <div className="mt-4 px-6">
                        <div className="text-center flex justify-center flex-col items-center space-y-5">
                            <img
                                src="https://storage.googleapis.com/heysynth_files/synth_webapp/Caution_sign_g1u9nv.svg"
                                alt="caution"
                                className="w-[60px] h-[53px]"
                            />
                            <div>
                                <h3 className="text-2xl mb-2 text-black font-rocGroteskMedium">
                                    Rejected
                                </h3>
                                <p className="text-slate-500 text-center mt-1 text-sm font-rocGroteskMedium">
                                    This purchase order will be rejected by you, kindly add a
                                    comment to complete rejection
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 flex gap-3">
                            <Button
                                btnText={"Close"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !text-gm-50"}
                                isLoading={false}
                                onClick={() => {
                                    setOpentRejectModal(false);
                                }}
                                disabled={loadingPOApprovalOrRejection}
                            />
                            <Button
                                onClick={() => setOpentRejectionReasonModal(true)}
                                btnText={"Add Comment"}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_purchase_order,
                                ]}
                                type={"button"}
                                btnClassname={"!bg-r-50 !text-white"}
                                disabled={loadingPOApprovalOrRejection}
                                isLoading={loadingPOApprovalOrRejection}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
            <ModalContainer
                open={openApprovalSuccessModal && selectedPO?._id === po?._id}
                showCloseIcon={false}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[500px]"
                closeModal={() => {
                    setOpenApprovalSuccessModal(false);
                }}
            >
                <div className="bg-white !px-2 p-8  rounded-[8px]">
                    <div className="mt-4 px-6">
                        <div className="text-center flex justify-center flex-col items-center space-y-5">
                            <div className="w-16 h-16 flex justify-center items-center bg-[#00DB8F] rounded-full">
                                <i className="ri-checkbox-circle-fill text-[29px] text-white"></i>
                            </div>
                            <div>
                                <h3 className="text-2xl mb-2 text-black font-rocGroteskMedium">
                                    Success
                                </h3>
                                <p className="text-slate-500 text-center mt-1 text-sm font-rocGroteskMedium">
                                    Request forwarded to next approver successfully. Approver will
                                    be notified of the pending task.Your request is pending the
                                    approval of the next level and you will be notified upon
                                    completion
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 flex gap-3">
                            <Button
                                btnText={"Close"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !text-gm-50"}
                                isLoading={false}
                                onClick={() => {
                                    setOpenApprovalSuccessModal(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
            <ModalContainer
                open={openRejectionReasonModal && selectedPO?._id === po?._id}
                showCloseIcon={false}
                modalClassName={"py-10 px-10"}
                tailwindClassName="w-[500px]"
                closeModal={() => {
                    setOpentRejectionReasonModal(false);
                    setRejectionReason("");
                }}
            >
                <div className="bg-white !px-2 p-8  rounded-[8px]">
                    <div className="mt-4 px-6">
                        <div className="text-center flex justify-center flex-col items-center">
                            <div className="mb-8">
                                <h3 className="text-2xl mb-2.5 text-black font-rocGroteskMedium">
                                    Provide a comment to reject this PO
                                </h3>
                                <p className="text-slate-500 text-center mt-1 text-sm font-rocGroteskMedium">
                                    To ensure transparency and compliance, please provide a brief
                                    comment explaining your decision to rejecting this PO.
                                </p>
                            </div>

                            <div className="w-full">
                                <TextareaInput
                                    name={"rejectionReason"}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={5}
                                    placeholder={"Comment"}
                                    required={false}
                                    containerClassname=""
                                />
                            </div>
                        </div>

                        <div className="mt-5 flex flex-col items-center gap-2.5">
                            <Button
                                onClick={() => handleApproveRejectPO("Reject", rejectionReason)}
                                btnText={"Send"}
                                btnPermission={[
                                    AccountPermissions.all_access,
                                    AccountPermissions.create_purchase_order,
                                ]}
                                type={"button"}
                                disabled={loadingPOApprovalOrRejection}
                                isLoading={loadingPOApprovalOrRejection}
                            />
                            <Button
                                btnText={"Close"}
                                type={"button"}
                                btnClassname={"!bg-transparent !text-gm-50 !w-fit"}
                                isLoading={false}
                                onClick={() => {
                                    setOpentRejectionReasonModal(false);
                                    setRejectionReason("");
                                }}
                                disabled={loadingPOApprovalOrRejection}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </>
    );
}
